import { Link, NavLink, useLocation } from "react-router-dom";

import { cn } from "@/lib/utils";
import { nvButtonVariants } from "./nv-button";
import { NvPersonIcon } from "./nv-icons";
import { NV_PATHS, nvLoginPath } from "./nv-paths";

// Figma "GNB / 화면 정의서 기준" (Type = Guest · Buyer · Supplier · PwC), 1440 x 72.
// "승인 대기" users see the Guest header (Front 화면설계서 slide 23).
export type NvGnbUserType = "guest" | "buyer" | "supplier" | "pwc";

interface MenuItem {
  label: string;
  to: string;
  phase2?: boolean;
}

const AGENT_FINDER: MenuItem = {
  label: "Agent Finder",
  to: NV_PATHS.agentFinder,
};
const CASE_STUDY: MenuItem = { label: "Case Study", to: NV_PATHS.caseStudy };
const AI_ASSISTANT: MenuItem = {
  label: "AI Assistant",
  to: NV_PATHS.aiAssistant,
};
const AGENT_REGISTER: MenuItem = {
  label: "Agent 등록",
  to: NV_PATHS.agentRegister,
  phase2: true,
};
const PRODUCT_MANAGE: MenuItem = {
  label: "Product 관리",
  to: NV_PATHS.productManage,
  phase2: true,
};

// Supplier menu follows Figma / 화면설계서 (no Agent Finder); IA rows 24 · 52 differ — open question B4.
const MENUS: Record<NvGnbUserType, MenuItem[]> = {
  guest: [AGENT_FINDER, CASE_STUDY, AI_ASSISTANT],
  buyer: [AGENT_FINDER, CASE_STUDY, AI_ASSISTANT],
  supplier: [CASE_STUDY, AGENT_REGISTER, PRODUCT_MANAGE],
  pwc: [AGENT_FINDER, CASE_STUDY, AI_ASSISTANT, AGENT_REGISTER],
};

// Menus and Contact Us share one style (ver.6): 14/17 #1A1C21; the current page turns medium + underlined.
const menuLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "text-[14px] leading-[17px] text-nv-text-primary",
    isActive
      ? "font-medium underline underline-offset-4"
      : "font-normal hover:underline hover:underline-offset-4",
  );

// My Page (ver.6): white + 1px #FD5108 + dark label; on its own pages it turns into a filled brand button.
const myPageLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "inline-flex items-center gap-[6px] rounded-nv-chip border border-nv-brand px-[14px] py-[7px] text-[13px] font-bold leading-4",
    isActive
      ? "bg-nv-brand text-nv-text-inverse"
      : "bg-nv-surface text-nv-text-primary hover:bg-nv-brand-subtle",
  );

export interface NvGnbProps {
  userType: NvGnbUserType;
  userName?: string;
  onLogout?: () => void;
  // 2차 menus (Agent 등록 · Product 관리) are hidden in the 1차 release.
  showPhase2?: boolean;
  className?: string;
}

// After a session ends on a public page the header turns into the guest header: its login link takes the
// current page along (?redirect=) so signing in again returns here. HOME, login and signup need no return address.
function useLoginLinkTarget() {
  const { pathname, search } = useLocation();
  const needsNoReturn =
    pathname === NV_PATHS.home ||
    pathname.startsWith(NV_PATHS.login) ||
    pathname.startsWith(NV_PATHS.signup);
  return nvLoginPath(needsNoReturn ? undefined : pathname + search);
}

function NvGnb({
  userType,
  userName,
  onLogout,
  showPhase2 = false,
  className,
}: NvGnbProps) {
  const { pathname } = useLocation();
  const agentEntry = pathname.match(/^\/agents\/([^/]+)/)?.[1];
  const caseEntry = pathname.match(/^\/case-study\/([^/]+)$/)?.[1];
  const assistantTarget = agentEntry
    ? `${NV_PATHS.aiAssistant}?agent_id=${encodeURIComponent(agentEntry)}`
    : caseEntry
      ? `${NV_PATHS.aiAssistant}?case_study_id=${encodeURIComponent(caseEntry)}`
      : NV_PATHS.aiAssistant;
  const menus = MENUS[userType].filter((item) => showPhase2 || !item.phase2);
  const isGuest = userType === "guest";
  const loginTo = useLoginLinkTarget();

  return (
    // Figma draws the bottom stroke inside the 72px frame, so the border is part of h-nv-gnb.
    // 스크롤해도 맨 위에 붙어요(sticky). z-40: 페이지 안 sticky 바(z-30)보다 위, 모달 오버레이(z-50)보다 아래.
    <header
      className={cn(
        "sticky top-0 z-40 h-nv-gnb w-full border-b border-nv-border-gnb bg-nv-surface",
        className,
      )}
    >
      <div className="flex h-full items-center justify-between px-10">
        <Link
          to={NV_PATHS.home}
          className="flex items-center"
          aria-label="AI Agent Navigator 홈"
        >
          {/* Figma image fill (213 x 30, @2x asset); the older -v2.png has wide transparent margins. */}
          <img
            src="/images/logos/logo-ai-agent-navigator-gnb.png"
            alt="PwC AI Agent Navigator"
            width={213}
            height={30}
            className="h-[30px] w-[213px] object-contain"
          />
        </Link>

        <nav aria-label="주 메뉴" className="flex items-center gap-10">
          {menus.map((item) => (
            <NavLink
              key={item.to}
              to={item === AI_ASSISTANT ? assistantTarget : item.to}
              className={menuLinkClass}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {isGuest ? (
          <div className="flex items-center gap-5">
            <NavLink to={NV_PATHS.contact} className={menuLinkClass}>
              Contact Us
            </NavLink>
            <Link
              to={loginTo}
              className={cn(
                nvButtonVariants({ variant: "primary", size: "sm" }),
                "rounded-nv-chip",
              )}
            >
              로그인/회원가입
            </Link>
          </div>
        ) : (
          <div className="flex items-center gap-[14px]">
            <NavLink to={NV_PATHS.contact} className={menuLinkClass}>
              Contact Us
            </NavLink>
            <span aria-hidden className="h-3 w-px bg-nv-border-strong" />
            {userName && (
              <span className="text-[13px] leading-4 text-nv-text-secondary">
                {userName}님
              </span>
            )}
            <NavLink to={NV_PATHS.myPage} className={myPageLinkClass}>
              <NvPersonIcon className="shrink-0" />
              My Page
            </NavLink>
            <button
              type="button"
              onClick={onLogout}
              className="text-[13px] leading-4 text-nv-text-disabled hover:text-nv-text-secondary"
            >
              로그아웃
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

export { NvGnb };
